import { useState } from "react";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import "./index.css";
import Cookies from "js-cookie";
import { Loader2 } from "lucide-react";

const TextEditor = ({
  initialValue,
  onChange,
  height,
  placeholder = "Type content....",
  label,
  error,
  ...props
}: any) => {
  const [content, setContent] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const handleChange = (value: any) => {
    if (value === "<p><br></p>") {
      setContent("");
    } else {
      setContent(value);
    }
    if (onChange) {
      onChange(value);
    }
  };

  // Function to upload the image to ImgBB (example)
  async function uploadImageToAWS(imageFile: any) {
    // Prepare form data
    setLoading(true); // Step 2: Start loader

    const formData = new FormData();
    formData.append("image", imageFile);
    try {
      const response = await fetch(`${baseURL}/custom-gallery/`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${
            sessionStorage.getItem("access") || Cookies.get("access")
          }`,
        },
      });
      const result = await response.json();
      if (response.ok) {
        return result;
      } else {
        throw new Error(result.error.message);
      }
    } catch (error) {
      console.error("Error uploading image to ImgBB:", error);
    } finally {
      setLoading(false); // Step 3: Stop loader in all cases
    }
  }

  const options = [
    ["bold", "italic", "underline", "strike"],
    ["align", "list", "table", "link", "image"],
    ["fontSize", "formatBlock", "blockquote", "lineHeight"],
  ];

  // const editor = useRef();

  // const getSunEditorInstance = (sunEditor) => {
  //   editor.current = sunEditor;
  // };

  const handleImageUploadBefore = async (files: any, info: any, uploadHandler: any) => {
    let response = {};
    // console.log("files", files);

    uploadImageToAWS(files[0]).then((img) => {
      response = {
        result: [
          {
            url: img?.image,
            name: img?.name || "Imagem",
            size: 400,
            width: 400,
            height: 400,
          },
        ],
      };
      // console.log("response", response);

      uploadHandler(response);
    });
  };

  return (
    <>
      <div
        className={`${props.className} w-full relative flex flex-col   ${
          error ? "required" : ""
        }`}
      >
        {label && <label className="fs-14-600 mb-1">{label}</label>}
        <SunEditor
          className="!break-all "
          name={props.name}
          setOptions={{
            buttonList: options,
          }}
          {...props}
          // ref={editor}
          // getSunEditorInstance={getSunEditorInstance}
          onImageUploadBefore={handleImageUploadBefore} // Pass function reference here
          placeholder={placeholder}
          height={height || 300}
          defaultValue={initialValue}
          onChange={handleChange}
          setContents={initialValue || content}
        />
        {loading && (
          <Loader2 className="absolute z-50 text-red-600 animate-spin transition-all top-1/2 left-1/2" />
        )}
        {error && (
          <span className="fs-12-600 mt-1 text-[14px] text-red-400">
            {error}
          </span>
        )}
      </div>
    </>
  );
};

export default TextEditor;

import { cn } from "@/lib/utils";

import { isEditorContentEmpty } from "@utils/helper/index.helper";
import { Editor, HTMLParser, Toolbar } from "lakelib";
import "lakelib/lib/lake.css";
import { useEffect, useRef } from "react";
import { baseURL } from "@services/index.services";
type Props = {
  value?: string;
  onChange: (e: string) => void;
  error?: string;
  className?: string;
  showLength?: string;
  [key: string]: any;
};

const RichTextEditor: React.FC<Props> = ({
  onChange,
  className,
  value = "",
  showLength,
  ...props
}) => {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<any>(null); // holds the editor instance

  const cleanedValue = isEditorContentEmpty(value) ? "" : value;

  useEffect(() => {
    if (!toolbarRef.current || !contentRef.current) return;

    const toolbar = new Toolbar({
      // @ts-ignore
      root: toolbarRef.current,
      items: [
        "bold",
        "italic",
        "underline",
        "|",
        "list",

        "|",
        "heading",
        "paragraph",
        "blockQuote",
        "|",
        "fontColor",
        "highlight",
        "|",
        "link",
        "image",
        "file",
        "equation",
        "|",
        "codeBlock",
        "hr",
        "formatPainter",
        "moreStyle",
        "|",
        "selectAll",
      ],
    });

    const editor = new Editor({
      // @ts-ignore
      readonly: props.readonly || false,
      root: contentRef.current,
      toolbar,
      placeholder: "Enter description",
      height: props.height ? `${props.height}px` : "300px",
      value: cleanedValue,
      image: {
        requestMethod: "POST",
        requestFieldName: "image",
        requestAction: `${baseURL}/custom-gallery/`,
        requestTypes: ["image/gif", "image/jpeg", "image/png"],
        transformResponse: (body: any) => ({ url: body.image }),
      },
      file: {
        requestMethod: "POST",
        requestFieldName: "image",
        requestAction: `${baseURL}/custom-gallery/`,
        transformResponse: (body: any) => ({ url: body.image }),
      },
    });

    editor.render();
    editorRef.current = editor;

    editor.event.on("change", (val: string) => {
      const next = isEditorContentEmpty(val) ? "" : val;
      onChange(next); // call once
    });

    return () => {
      editor.unmount?.();
      editorRef.current = null;
    };
    // IMPORTANT: run ONCE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When external value changes (e.g., form reset), update editor content without re-creating it
  useEffect(() => {
    const inst = editorRef.current;
    if (!inst) return;
    if (inst && inst.getValue() !== cleanedValue) {
      inst.setValue(cleanedValue);
    }
    // Avoid feedback loop: only set if different
    if (
      typeof inst.getValue === "function" &&
      inst.getValue() !== cleanedValue
    ) {
      inst.setValue?.(cleanedValue);
    }
  }, [cleanedValue]);

  const parser = new HTMLParser(value);
  const fragment = parser.getFragment();

  return (
    <div className="my-editor">
      <div className="my-toolbar  border-b flex flex-wrap" ref={toolbarRef} />
      <div
        ref={contentRef}
        className={cn(
          "my-content outline-none  p-3 content-block break-all  font-fustat",
          className,
        )}
        style={{
          position: "relative",
          minHeight: props.height ? `${props.height}px` : "200px",
        }}
      />
      {showLength && (
        <div className="p-2">
          {showLength && (fragment.textContent?.split("").length ?? 0)}
        </div>
      )}
    </div>
  );
};

export { RichTextEditor };
